import { createClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || url.includes("YOUR_PROJECT_ID")) {
  throw new Error(
    "[Supabase] VITE_SUPABASE_URL is not set.\n" +
      "1. Go to https://supabase.com → Your Project → Settings → API\n" +
      "2. Copy the Project URL into .env.local as VITE_SUPABASE_URL",
  );
}

if (!key || key.includes("YOUR_ANON_KEY")) {
  throw new Error(
    "[Supabase] VITE_SUPABASE_ANON_KEY is not set.\n" +
      "1. Go to https://supabase.com → Your Project → Settings → API\n" +
      "2. Copy the anon/public key into .env.local as VITE_SUPABASE_ANON_KEY",
  );
}

/**
 * Supabase client — import this everywhere you need database access.
 *
 * The client automatically:
 *  - Stores the user session in localStorage (under sb-* keys)
 *  - Refreshes the JWT token before it expires
 *  - Applies Row Level Security based on the logged-in user
 */
export const supabase = createClient<Database>(url, key, {
  auth: {
    // Persist the session across page refreshes
    persistSession: true,
    // Auto-refresh the access token before it expires
    autoRefreshToken: true,
    // Detect session from URL (needed for magic links / OAuth redirects)
    detectSessionInUrl: true,
  },
});

/**
 * Call this before any authenticated DB write.
 *
 * Uses getUser() — not getSession() — because getSession() reads from the
 * local cache and can return a stale token that Chromium browsers (Edge,
 * Chrome) don't attach to PostgREST requests, causing auth.uid() to return
 * NULL in Postgres and triggering a 42501 RLS violation.
 *
 * getUser() makes a live round-trip to the Supabase auth server, validates
 * the JWT, triggers an automatic token refresh if needed, and guarantees
 * the client's internal Authorization header is up-to-date before the write.
 */
export async function requireSession(expectedUserId: string): Promise<void> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    // "Auth session missing!" occurs right after signup when email confirmation
    // is enabled — there is no active session yet.  The DB write that follows
    // will be rejected by Supabase RLS anyway, so we log but do not throw here.
    // All other auth errors are still surfaced.
    if (
      error.message.toLowerCase().includes("auth session missing") ||
      error.code === "session_not_found"
    ) {
      console.warn(
        "[requireSession] No active session — skipping guard (RLS will enforce auth).",
      );
      return;
    }
    console.error("[requireSession] getUser error:", error.message);
    throw new Error(`Auth session error: ${error.message}`);
  }

  if (!user) {
    console.error("[requireSession] No authenticated user — JWT not valid.");
    throw new Error(
      "Not authenticated — please refresh the page and log in again.",
    );
  }

  if (user.id !== expectedUserId) {
    console.error("[requireSession] User ID mismatch — session desync.");
    throw new Error(
      "Session mismatch — please log out and back in.",
    );
  }
}
